import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [verificationError, setVerificationError] = useState('');

  const navigate = useNavigate();

  const { setAuth } = useAuth();

  // Handle login request
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    const payload = { email, password };

    try {
      const response = await fetch(
        'https://academichub-backend.onrender.com/auth/login/verifyemail',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        console.log('Login successful: Verification code sent.');
        setIsModalOpen(true); // Open verification modal
      } else {
        // Handle different error responses
        const data = await response.json();
        setError(data.error || 'Login failed. Please try again.');
        console.error('Login failed:', data.error);
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      console.error('Error during login:', error);
    }
  };

  // Handle code input
  const handleCodeChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input if a digit is entered
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle code verification
  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError(''); // Clear any previous errors

    const verificationCode = code.join('');
    const payload = { email, code: verificationCode };

    try {
      const response = await fetch(
        'https://academichub-backend.onrender.com/auth/login/verifycode',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        console.log('Verification successful!');
        setIsModalOpen(false);

        setAuth(true);
        navigate('/'); // Redirect after successful verification
      } else {
        // Handle verification error
        const data = await response.json();
        setVerificationError(data.error || 'Verification failed. Please try again.');
        console.error('Verification failed:', data.error);
      }
    } catch (error) {
      setVerificationError('An error occurred. Please try again later.');
      console.error('Error during code verification:', error);
    }
  };

  // Handle navigating to forgot password page
  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center w-full">
      <div className="p-8 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-center">
          <img src="/assets/logo.svg" alt="Logo" className="w-52 h-auto" />
        </div>
        <h1 className="text-5xl font-semibold text-center text-white mb-6">Login</h1>
        <p className="text-center text-white mb-6">
          Welcome back to AcademicHub. Did you{' '}
          <button onClick={handleForgotPassword} className="text-teal-400 hover:underline">
            forget your password?
          </button>
        </p>

        {/* Login Form */}
        {!isModalOpen && (
          <>
            {error && <div className="text-red-500 p-3 rounded-md mb-4">{error}</div>}
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              >
                Continue
              </button>
            </form>
          </>
        )}

        {/* Verification Code Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Verification Code</h2>
              <p className="text-gray-600 mb-6">
                Enter the 6-digit code we emailed to your institution email.
              </p>
              {verificationError && (
                <div className="bg-red-500 text-white p-3 rounded-md mb-4">{verificationError}</div>
              )}
              <form onSubmit={handleCodeVerification} className="space-y-6">
                <div className="flex justify-between">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeChange(index, e.target.value)}
                      className="w-10 h-10 text-center text-lg bg-gray-200 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  Verify Code
                </button>
              </form>
            </div>
          </div>
        )}

        <p className="text-center text-gray-400 mt-4">
          <button onClick={() => navigate('/signup')} className="text-teal-400 hover:underline">
            Create an account
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
